import clsx from 'clsx';
import ChatManager from '../Chatbot/ChatManager';
import * as customizationService from '@/services/customization.service';
import { useEffect, useState, useMemo } from 'react';
import useDanteApi from '@/hooks/useDanteApi';
import DButtonIcon from '../Global/DButtonIcon';
import DashboardIcon from '../Global/Icons/DashboardIcon';
import { useCustomizationStore } from '@/stores/customization/customizationStore';
import { CHATBOTS_WITH_NEW_DESIGN, COMMON_CLASSNAMES, DANTE_THEME_CHAT, SYSTEM_FONTS } from '@/constants';
import { useConversationStore } from '@/hooks/useConversationStore';
import { useTimezoneStore } from '@/stores/timezone/timezoneStore';
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react';
import HomeContent from '../Bubble/HomeContent';
import useIsAboveBreakpoint from '@/helpers/useIsAboveBreakpoint';
import CloseIcon from '../Global/Icons/CloseIcon';
import { useSearchParams } from 'react-router-dom';
import DToastContainer from '../DToast/DToastContainer';
import DChatPassword from '../DChatPassword';
import { useRef } from 'react';
import generateGoogleFonts from '@/helpers/generateGoogleFonts';
import StyleTag from '../StyleTag';
import DLoaderBubbleLoading from '../Global/DLoaderBubbleLoading';
import DModalEmailTransaction from '../DModalEmailTransaction';
import { trackEvent } from '@/helpers/trackEvent';
import { checkDomainBlocked } from '@/helpers/domainWhitelist';
import DomainBlockedMessage from '../DomainBlockedMessage';
import HelperScriptRequiredMessage from '../HelperScriptRequiredMessage';
import HomeTabContent from '@/v3/components/UI/HomeTabContent';
import FullDanteLogoGreyIcon from '@/v3/components/UI/Icons/FullDanteLogoGreyIcon';
import DanteLogoIcon from '@/v3/components/UI/Icons/DanteLogoIcon';
import BubblePreviousConversations from '@/v3/components/UI/BubblePreviousConversations';
import VerticalOptionsMenuIcon from '@/v3/components/UI/Icons/VerticalOptionsMenuIcon';
import MuteSoundIcon from '@/v3/components/UI/Icons/MuteSound';
import SoundIcon from '@/v3/components/UI/Icons/SoundIcon';
import EmailTranscriptIcon from '@/v3/components/UI/Icons/EmailTranscriptIcon';
import DocsIcon from '@/v3/components/UI/Icons/DocsIcon';
import ResetIcon from '@/v3/components/UI/Icons/ResetIcon';
import OnlineStatusIcon from '@/v3/components/UI/Icons/OnlineStatusIcon';
import { useSoundEffects } from '@/hooks/useSoundEffects';

const BubbleDirectLink = ({
  config,
  isInApp = false,
  initialShouldFetchCustomization = false,
  isPreviewMode = false,
}) => {
  const [searchParams] = useSearchParams();
  const { data: customizationData, isLoading: isLoadingCustomization } =
    useDanteApi(
      customizationService.getSharedChatbotCustomizationById,
      [],
      {},
      {
        kb_id: config?.kb_id,
        token: config?.token,
      }
    );

  const timeoutRef = useRef(null);

  const isAbovePreview = useIsAboveBreakpoint('preview');
  const [showPasswordModal, setShowPasswordModal] = useState(
    customizationData?.public === false
  );
  const [loading, setLoading] = useState(true);
  const [showEmailTranscript, setShowEmailTranscript] = useState(false);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [isDomainBlocked, setIsDomainBlocked] = useState(false);
  const [parentPageInfo, setParentPageInfo] = useState(null);
  const [helperScriptDetected, setHelperScriptDetected] = useState(false);
  const [blockingReason, setBlockingReason] = useState(null); // 'helper-script-missing' or 'domain-blocked'

  const [cookie, setCookie] = useState(null);
  const [showPreviousConversations, setShowPreviousConversations] = useState(false);
  const params = new URLSearchParams(window.location.search);

  // Use the conversation store with automatic namespacing
  const { resetCurrentConversation, setCurrentConversation } = useConversationStore(config?.kb_id, 'direct-link');

  const { setChatbotCustomization, chatbotCustomization } =
    useCustomizationStore();

  const timezone = useTimezoneStore((state) => state.timezone);

  // Initialize sound effects with config data
  const { soundConfig, updateSoundConfig } = useSoundEffects({ customizationData: chatbotCustomization });

  // Sound toggle function
  const toggleSound = () => {
    updateSoundConfig({
      enabled: !soundConfig.enabled
    });
  };

  // Handle dropdown item clicks
  const handleDropdownItemClick = (item) => {
    if (item.label === 'Reset Chat') {
      handleResetConversation();
    } else if (item.label === 'Privacy Policy') {
      const url = chatbotCustomization?.privacy_policy_link;
      window.open(url, '_blank');
    } else if (item.label === 'Terms of Service') {
      const url = chatbotCustomization?.terms_of_use_link ;
      window.open(url, '_blank');
    } else if (item.label === 'Email Transcript') {
      setShowEmailTranscript(true);
    } else if (item.label === 'Mute Notifications' || item.label === 'Unmute Notifications') {
      toggleSound();
    }
  };



  // Clear conversation on mount for fresh conversation on every page load
  // Skip reset for preview mode to allow testing conversations in dashboard
  useEffect(() => {
    if (!isPreviewMode && !isInApp) {
      resetCurrentConversation();
    }
  }, [resetCurrentConversation, isPreviewMode, isInApp]);

  // PostMessage communication with parent page
  useEffect(() => {
    const isInIframe = window.parent !== window;

    if (isInIframe) {

      // Listen for messages from parent (Dante Helper Script)
      const handleMessage = (event) => {
        const { data } = event;

        if (data.type === 'DANTE_PAGE_INFO') {
          setParentPageInfo(data);
          setHelperScriptDetected(true);
        }
      };

      window.addEventListener('message', handleMessage);

      // Register with parent and request page info
      const registerWithParent = () => {
        window.parent.postMessage({
          type: 'DANTE_IFRAME_READY',
          timestamp: Date.now()
        }, '*');

        // Request page info
        setTimeout(() => {
          window.parent.postMessage({
            type: 'DANTE_REQUEST_PAGE_INFO',
            timestamp: Date.now()
          }, '*');
        }, 100);
      };

      // Register immediately and after a delay
      registerWithParent();
      const timeoutId = setTimeout(registerWithParent, 500);

      return () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(timeoutId);
      };
    }
  }, []);

  useEffect(() => {
    if (
      customizationData &&
      !isLoadingCustomization &&
      Object.keys(customizationData).length > 2
    ) {
      setChatbotCustomization(customizationData);

      // Check domain whitelist for iframe (skip for dashboard previews and in-app usage)
      const whitelistedDomains = customizationData?.whitelisted_domains || [];

      if (whitelistedDomains.length > 0 && !isPreviewMode && !isInApp) {
        const isInIframe = window.parent !== window;

        if (isInIframe) {

          // Wait a bit for helper script to respond
          setTimeout(() => {
            if (!helperScriptDetected) {
              setIsDomainBlocked(true);
              setBlockingReason('helper-script-missing');
            } else if (parentPageInfo) {
              const parentUrl = parentPageInfo.url;
              const isBlocked = checkDomainBlocked(parentUrl, whitelistedDomains);

              if (isBlocked) {
                setIsDomainBlocked(true);
                setBlockingReason('domain-blocked');
              }
            }
          }, 1000); // Give helper script time to respond
        } else {
          // Not in iframe, use current URL
          const currentUrl = window.location.href;
          const isBlocked = checkDomainBlocked(currentUrl, whitelistedDomains);
          setIsDomainBlocked(isBlocked);
        }
      }
    }
  }, [customizationData, isLoadingCustomization, setChatbotCustomization]);

  const handleResetConversation = () => {
    resetCurrentConversation();
    window.location.reload();
  };

  const customizationFontFamily = useMemo(() => {
    return generateGoogleFonts(
      chatbotCustomization.font_name || DANTE_THEME_CHAT.font_name
    );
  }, [chatbotCustomization]);

  useEffect(() => {
    setShowPasswordModal(customizationData?.public === false);
  }, [customizationData]);

  useEffect(() => {
    if (isLoadingCustomization && (!config?.kb_id || !config?.token)) {
      setLoading(true);
      clearTimeout(timeoutRef.current);
    } else {
      timeoutRef.current = setTimeout(() => {
        setLoading(false);
      }, 3000);
    }
  }, [isLoadingCustomization, config]);

  const memoizedConfig = useMemo(() => ({
    ...config,
    ...chatbotCustomization,
    id: searchParams.get('kb_id'),
    parentUrl: searchParams.get('parentUrl') || config?.parentUrl || '',
  }), [config, chatbotCustomization, searchParams]);

  // Track chatbot_shared event for direct link access
  useEffect(() => {
    if (memoizedConfig?.kb_id && !isInApp && !isPreviewMode) {
      try {
        trackEvent('chatbot_shared', {
          kb_id: memoizedConfig.kb_id,
          source: 'direct_link'
        });
      } catch (error) {
        console.error('Failed to track chatbot_shared event:', error);
      }
    }
  }, [memoizedConfig?.kb_id, isInApp, isPreviewMode]);

  return (
    <>
      <Transition show={loading}>
        <div
          className={clsx(
            'transition-all duration-200 data-[closed]:opacity-0 data-[enter]:delay-200',
            'flex flex-col w-full h-full items-center'
          )}
        >
          <DLoaderBubbleLoading 
            show={true} 
            elementColor={chatbotCustomization?.element_color || DANTE_THEME_CHAT.element_color}
            size={64}
          />
        </div>
      </Transition>
      <Transition show={!loading}>
        <div
          className={clsx(
            {'py-size3': CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id)},
            'embed',
            'flex flex-col w-full h-full items-center',
            'transition-all duration-200 data-[closed]:opacity-0 data-[enter]:delay-200'
          )}
          style={{
            backgroundColor: 'var(--dt-color-surface-100)',
          }}
        >
          {isDomainBlocked ? (
            blockingReason === 'helper-script-missing' ? (
              <HelperScriptRequiredMessage />
            ) : (
              <DomainBlockedMessage parentUrl={parentPageInfo?.url || document.referrer || window.location.href} />
            )
          ) : (
            <>
              <div className="w-full flex flex-col items-end px-size2">
                <DToastContainer />
              </div>
              <StyleTag
                tag="body"
                tempCustomizationData={chatbotCustomization}
                customImport={customizationFontFamily}
              />
          {config?.kb_id !== 'ca7ac107-9716-4ef3-a6b5-55ceab49f15a' && <header className={clsx("flex items-center justify-between py-size3 px-size5 sm:px-size2 w-full max-w-screen-xl", {'pt-0 !px-size2': CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id)})}>
            <div className="flex items-center justify-between h-[25px]">
              <Transition show={chatbotCustomization?.embed_logo_url}>
                <div className="transition-all duration-200 data-[closed]:opacity-0 data-[enter]:delay-200">
                  <img
                    src={chatbotCustomization?.embed_logo_url}
                    alt="logo"
                    className={clsx(
                      'w-[95px] h-[25px] object-contain object-left transition-all',
                      COMMON_CLASSNAMES.transition.duration.medium,
                      {
                        'opacity-0': !chatbotCustomization?.embed_logo_url,
                      }
                    )}
                  />
                </div>
              </Transition>
              <Transition show={!chatbotCustomization?.embed_logo_url}>
                <div className="transition-all duration-200 data-[closed]:opacity-0 data-[enter]:delay-200">
                  <DanteLogoIcon />
                </div>
              </Transition>
            {/* Chatbot name */}
            <div className='flex flex-col max-w-[200px] ml-3 embed-header-name-wrapper'>
              <div className='flex items-center gap-[6px]'>
                <span
                  className="embed-chatbot-name text-sm font-medium text-v3-black-max truncate"
                  style={{
                    fontFamily: memoizedConfig?.font_name ? `${memoizedConfig.font_name}, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif` : undefined,
                    fontSize: memoizedConfig?.font_size ? `${memoizedConfig.font_size}px` : undefined,
                    color: memoizedConfig?.element_color || 'var(--v3-black-max)',
                  }}
                >
                  {chatbotCustomization?.chatbot_name || 'Dante AI'}
                </span>
                <OnlineStatusIcon className="flex-shrink-0" />
              </div>
              <div
                className='text-xs text-v3-black-secondary leading-4 font-medium'
                style={{
                  fontFamily: memoizedConfig?.font_name || undefined,
                  color: memoizedConfig?.element_color ? `${memoizedConfig.element_color}B3` : 'var(--v3-black-secondary)',
                }}
              >
                AI Agent
              </div>
            </div>
            </div>
            {(params.get('header') !== 'false') && <div className={clsx("flex items-center justify-between gap-size1 no-scrollbar relative", {'gap-v3-xs': CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id)})}>
              {!CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) && (
                <>
                  {/* <DButtonIcon variant="ghost" onClick={handleResetConversation}>
                    <ResetIcon width={16} height={16} className={CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) ? 'size-5 font-bold' : ''}/>
                  </DButtonIcon> */}
                  {chatbotCustomization?.home_tab_enabled && (
                <Menu>
                  {!CHATBOTS_WITH_NEW_DESIGN.includes(config?.kb_id) && <MenuButton className="bg-transparent border border-[--dt-color-element-10] text-[--dt-color-element-100] size-8 flex items-center justify-center rounded-size0">
                    <DashboardIcon width={16} height={16} />
                  </MenuButton>}
                  <MenuItems
                    transition
                    className={clsx(
                      'fixed top-0 right-0 z-50', // fixed position at top right
                      'w-full sm:w-[424px] h-full',
                      'no-scrollbar pb-size3',
                      'bg-[--dt-color-surface-100] border-l border-[--dt-color-element-10] sm:border sm:rounded-size0 pb-size3',
                      'transition duration-200 ease-out data-[closed]:opacity-0'
                    )}
                  >
                    <MenuItem>
                      {({ close }) => (
                        <>
                          <DButtonIcon
                            variant="ghost"
                            className={clsx('w-v3-lg h-v3-lg backdrop-blur-lg absolute  z-50 right-v3-sm top-v3-sm flex justify-center items-center')}
                            onClick={(e) => {
                              e.stopPropagation();
                              close();
                            }}
                            style={{
                              color: 'var(--dt-color-element-100)',
                              borderColor: 'var(--dt-color-element-2)',
                              backgroundColor: 'var(--dt-color-element-2)',
                            }}
                          >
                            <CloseIcon />
                          </DButtonIcon>
                          <div className="h-full no-scrollbar w-full overflow-y-auto relative"  style={{
                            background: 'radial-gradient(243.51% 165.3% at -5% -5%, var(--dt-color-brand-40) 0%, var(--dt-color-surface-100) 40%)',
                          }}>
                            {showPreviousConversations && memoizedConfig?.previous_conversation_enabled ? (
                              <BubblePreviousConversations
                                kb_id={config?.kb_id}
                                isPreviewMode={isPreviewMode}
                                previous_conversation_enabled={memoizedConfig?.previous_conversation_enabled}
                                onConversationClick={() => {
                                  setShowPreviousConversations(false);
                                  close();
                                }}
                                onNavigate={(view) => {
                                  if (view === 'chat') {
                                    setShowPreviousConversations(false);
                                    close();
                                  }
                                }}
                                config={customizationData}
                                interfaceType="direct-link"
                                showBackButton={true}
                              />
                            ) : (
                              <HomeTabContent
                                kb_id={config?.kb_id}
                                token={config?.token}
                                config={customizationData}
                                previous_conversation_enabled={
                                  config?.previous_conversation_enabled
                                }
                                onNavigate={(view) => {
                                  if (view === 'chat') {
                                    setShowPreviousConversations(false);
                                    close();
                                  } else if (view === 'chat-previous-conversations') {
                                    setShowPreviousConversations(true);
                                  }
                                }}
                                interfaceType="direct-link"
                                isInIframe={true}
                              />
                            )}
                          </div>
                        </>
                      )}
                    </MenuItem>
                  </MenuItems>
                </Menu>
              )}
                </>
              )}
              {(params.get('header') !== 'false') && <Menu>
                <MenuButton
                  className="embed-dropdown-button flex w-v3-lg h-v3-lg justify-center items-center gap-v3-xs aspect-square rounded-v3-sm transition-colors hover:bg-v3-black-border"
                >
                  <VerticalOptionsMenuIcon className="embed-dropdown-icon" style={{
                    color: chatbotCustomization?.element_color || '#000000',
                  }} />
                </MenuButton>
                <MenuItems transition className="embed-dropdown-menu absolute top-full right-0 mt-v3-xs z-[9999] w-[205px] p-v3-xxs flex flex-col items-start rounded-v3-lg bg-[#FFFFFF] shadow-v3-double text-v3-black-max">
                  <div className="w-full">
                    {chatbotCustomization?.sound_enabled && (
                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => handleDropdownItemClick({
                              label: soundConfig.enabled ? 'Mute Notifications' : 'Unmute Notifications'
                            })}
                            className={`embed-dropdown-item embed-dropdown-sound w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                              active ? 'bg-v3-black-border' : 'bg-white'
                            }`}
                          >
                            {soundConfig.enabled ?
                              <MuteSoundIcon style={{ color: chatbotCustomization?.brand_color || '#5745FA' }} /> :
                              <SoundIcon style={{ color: chatbotCustomization?.brand_color || '#5745FA' }} />
                            }
                            <span>{soundConfig.enabled ? 'Mute Notifications' : 'Unmute Notifications'}</span>
                          </button>
                        )}
                      </MenuItem>
                    )}

                    {customizationData?.show_email_details && (
                      <MenuItem>
                        {({ active }) => (
                          <button
                            onClick={() => handleDropdownItemClick({
                              label: 'Email Transcript'
                            })}
                            className={`embed-dropdown-item embed-dropdown-email w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                              active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                            }`}
                          >
                            <EmailTranscriptIcon style={{
                              color: chatbotCustomization?.brand_color || '#5745FA'
                            }} />
                            <span>Email Transcript</span>
                          </button>
                        )}
                      </MenuItem>
                    )}

                    <MenuItem>
                      {({ active }) => (
                        <button
                          onClick={() => handleDropdownItemClick({
                            label: 'Reset Chat'
                          })}
                          className={`embed-dropdown-item embed-dropdown-reset-chat w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                            active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                          }`}
                        >
                          <ResetIcon style={{
                            color: chatbotCustomization?.brand_color || '#5745FA'
                          }} />
                          <span>Reset Chat</span>
                        </button>
                      )}
                    </MenuItem>

                    <MenuItem>
                      {({ active }) => (
                        <button
                          onClick={() => handleDropdownItemClick({
                            label: 'Terms of Service'
                          })}
                          className={`embed-dropdown-item embed-dropdown-terms-of-service w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                            active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                          }`}
                        >
                          <DocsIcon style={{
                            color: chatbotCustomization?.brand_color || '#5745FA'
                          }} />
                          <span>Terms of Service</span>
                        </button>
                      )}
                    </MenuItem>

                    <MenuItem>
                      {({ active }) => (
                        <button
                          onClick={() => handleDropdownItemClick({
                            label: 'Privacy Policy'
                          })}
                          className={`embed-dropdown-item embed-dropdown-privacy-policy w-full flex items-center gap-v3-xs p-v3-xs text-left text-sm font-medium transition-colors rounded-v3-lg ${
                            active ? 'bg-v3-black-border' : 'bg-[#FFFFFF]'
                          }`}
                        >
                          <DocsIcon style={{
                            color: chatbotCustomization?.brand_color || '#5745FA'
                          }} />
                          <span>Privacy Policy</span>
                        </button>
                      )}
                    </MenuItem>
                  </div>
                </MenuItems>
              </Menu>}
            </div>}
          </header>}
          <div className="flex flex-col w-full max-w-screen-lg h-full min-h-0">
            <ChatManager
              config={memoizedConfig}
              isInApp={isInApp}
              initialShouldFetchCustomization={false}
              isPreviewMode={isPreviewMode}
              cookie={cookie}
              setCookie={setCookie}
              setShowEmailTranscript={setShowEmailTranscript}
              showEmailTranscript={showEmailTranscript}
              isDirectLink={true}
            />
          </div>
          {!memoizedConfig?.remove_watermark && <div className='px-v3-sm w-full '>
            <div className='flex items-center justify-center w-full py-[12px]'>
              <div className="flex items-center gap-1 cursor-pointer" onClick={() => {
                window.open(`https://dante-ai.com/?utm_source=cht-${config?.kb_id}&utm_medium=pwdByDante&utm_campaign=poweredByDante`, '_blank');
              }}>
                <p className="text-xs font-medium text-v3-black-secondary leading-4">Powered by </p>
                <FullDanteLogoGreyIcon className="w-[56px] h-[14px]" />
              </div>
            </div>
          </div>}
          <DModalEmailTransaction
            open={showEmailTranscript}
            onClose={() => setShowEmailTranscript(false)}
            token={config?.token}
            setNotification={setNotification}
            notification={notification}
          />
          {customizationData?.public === false && showPasswordModal && (
            <div className="absolute bottom-0 left-0 right-0 h-full flex flex-col gap-size1 backdrop-blur-sm justify-end">
              <div className="bg-white p-size5 rounded-size1 flex flex-col gap-size1 z-1">
                <DChatPassword
                  kb_id={config?.kb_id}
                  setCookie={setCookie}
                  onUnlockSuccess={() => setShowPasswordModal(false)}
                />
              </div>
            </div>
          )}
            </>
          )}
        </div>
      </Transition>
    </>
  );
};

export default BubbleDirectLink;
